import { getElectricalItems } from "building-generation/elements/electrical/generatedElectricalUtils";
import type { Building } from "building-types/building/buildingTypes";
import type { ElectricalKeyAttributes } from "building-types/dataSequences/builtin/ElectricalKAM";
import type { ElectricalKAM, ElectricalKAMOptions } from "building-types/dataSequences/builtin/ElectricalKAM";
import type {
  GeneratedElectricalGroup,
  StandardElectricalItem,
} from "building-types/electrical/generatedElectricalTypes";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { keyAttributeNew, keyMeasureNew, keyAttributeOptionsNew } from "doc/utils/dataSequenceTools";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  DOC_ENTITY_COLUMN_NAME,
  SortBy,
  categoryColumnNew,
  configurableTableNew,
  identifierColumnNew,
  rowMetadataNew,
  stringColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";

export function makeElectricalKAM(item: StandardElectricalItem): ElectricalKAM {
  return {
    type: "ELECTRICAL",
    attributes: {
      type: keyAttributeNew(item.type),
      group: keyAttributeNew(item.group),
      entityName: keyAttributeNew(item.entityName),
    },
    measures: {
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}
export const EMPTY_ELECTRICAL_KAM: ElectricalKAM = {
  type: "ELECTRICAL",
  attributes: {
    type: keyAttributeNew(""),
    group: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    count: keyMeasureNew(0),
  },
  handles: [],
};
export function makeDefaultElectricalKAMOptions(): ElectricalKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {},
  };
}
export const defaultElectricalKAMSort = (a: ElectricalKAM, b: ElectricalKAM) => {
  return (
    a.attributes.group.value.localeCompare(b.attributes.group.value) ||
    a.attributes.type.value.localeCompare(b.attributes.type.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingElectricalKAMs(
  building: Building,
  filter?: (entity: GeneratedElectricalGroup) => boolean,
): ElectricalKAM[] {
  const electrical = getElectricalItems(building, filter);
  const result: ElectricalKAM[] = [];
  for (const itemId of Object.keys(electrical)) {
    result.push(makeElectricalKAM(electrical[itemId]));
  }
  return result;
}

export function makeElectricTable(building: Building): ConfigurableTable {
  const electricalItems = buildingElectricalKAMs(building);
  return configurableTableNew<ElectricalKAM, ElectricalKAM>({
    iterables: electricalItems,
    requirement: (item) => item,
    columns: [
      categoryColumnNew("Electrical"),
      stringColumnNew({
        name: "Group",
        fieldFunc: (item) => item.attributes.group.value,
        useInDiff: false,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Electrical Type",
        fieldFunc: (item) => item.attributes.type.value,
        sortBy: SortBy.Ascending,
      }),
    ],
    identifiers: [
      identifierColumnNew({
        name: DOC_ENTITY_COLUMN_NAME,
        identifierFunc: (item) => item.handles.map(docEntityHandleHash).join(",") || "0",
      }),
    ],
    rowMetadata: rowMetadataNew({
      metadataFunc: (item) => {
        const entityName = item.attributes.entityName.value;
        return { entityName, entityType: "ELECTRIC" };
      },
    }),
  });
}

// Attributes used in procure entityHash
export const DEFINES_ELECTRICAL: (keyof ElectricalKeyAttributes)[] = ["entityName"];
